import React, { useEffect, useMemo, useState } from 'react';
import { AddManageSitesPanel } from './SuperAdminPanel';
import { AddManageUsersPanel } from './AddManageUsersPanel';
import { SubmittedFormsPanel } from './SubmittedFormsPanel';
import { HazardsAndControlPanel } from './HazardsAndControlPanel';
import { SUBMITTED_AND_HAZARDS_SECTION, SUBMITTED_FORMS_VIEW, HAZARDS_CONTROL_VIEW } from '../config/dashboardViews';
import { SafetyFormPanel } from './SafetyFormPanel';
import {
  CONFIGURE_PROFILE_VIEW,
  CollapsibleAccountInfo,
  ConfigureProfilePanel,
} from './ConfigureProfilePanel';
import { LogoutButton } from './LogoutButton';
import { MobileActionBar, useIsDesktop } from './MobileActionBar';
import { DashboardCardSection } from './DashboardCardSection';
import { ScrollableRightPane } from './ScrollEdgeButtons';
import { CollapsibleSidebarNav } from './CollapsibleSidebarNav';
import { UserProfileProvider } from '../context/UserProfileContext';
import { CertificateNotificationsBell } from './CertificateNotificationsBell';
import CertificateUploadModal from './CertificateUploadModal';
import { apiUrl } from '../config/api';
import { getSafetyFormCards, isSafetyFormView, parseSafetyFormIndex } from '../config/safetyForms';
import { refreshCurrentUser } from '../utils/refreshCurrentUser';
import { getStoredUser } from '../utils/auth';
import { isAboveSupervisorRole } from '../utils/roles';

const sidebarSearchClass =
  'w-full mb-1.5 px-2.5 py-1.5 rounded-md bg-black border border-gray-700 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-amber-300/40 focus:border-amber-300/40';

function matchesFormSearch(card, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [card.title, card.description, card.frequencyNote]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

const ADMIN_SECTIONS = [
  {
    label: 'User/Workers & Sites/Projects',
    cards: [
      {
        id: 'add-manage-users',
        title: 'Add / Manage User/Workers',
        description: 'Add or manage User/Worker accounts.',
        icon: '👤',
      },
      {
        id: 'add-manage-sites',
        title: 'Add / Manage Sites/Projects',
        description: 'Add or manage sites/projects.',
        icon: '📍',
      },
    ],
  },
  SUBMITTED_AND_HAZARDS_SECTION,
];

function AdminHeaderActions({ showNotificationsBell, onOpenCertificateNotification }) {
  return (
    <>
      {showNotificationsBell ? (
        <CertificateNotificationsBell onOpenCertificate={onOpenCertificateNotification} />
      ) : null}
      <LogoutButton />
    </>
  );
}

function AdminHeaderToolbar(props) {
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;

  return (
    <div className="mb-2 flex items-center justify-end gap-2">
      <AdminHeaderActions {...props} />
    </div>
  );
}

function renderPanel(activeView, onBack, projectName, user, panelProps) {
  if (isSafetyFormView(activeView)) {
    const formIndex = parseSafetyFormIndex(activeView);
    return (
      <SafetyFormPanel formIndex={formIndex} projectName={projectName} onBack={onBack} />
    );
  }
  switch (activeView) {
    case 'add-manage-users':
      return (
        <AddManageUsersPanel
          onBack={onBack}
          user={user}
          createOpen={panelProps.userCreateOpen}
          onCreateOpenChange={panelProps.onUserCreateOpen}
        />
      );
    case 'add-manage-sites':
      return (
        <AddManageSitesPanel
          onBack={onBack}
          createOpen={panelProps.siteCreateOpen}
          onCreateOpenChange={panelProps.onSiteCreateOpen}
          companyScopeId={user?.company_id}
          companyScopeName={user?.company_name}
        />
      );
    case SUBMITTED_FORMS_VIEW:
      return (
        <SubmittedFormsPanel
          onBack={onBack}
          lockedCompanyId={user?.company_id}
          lockedCompanyName={user?.company_name}
        />
      );
    case HAZARDS_CONTROL_VIEW:
      return <HazardsAndControlPanel onBack={onBack} />;
    default:
      return null;
  }
}

function findActiveCard(activeView, adminSections, formCards) {
  const adminCard = adminSections.flatMap((s) => s.cards).find((c) => c.id === activeView);
  if (adminCard) return adminCard;
  return formCards.find((c) => c.id === activeView);
}

export function AdminDashboard({ user: initialUser }) {
  const [activeView, setActiveView] = useState('home');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userCreateOpen, setUserCreateOpen] = useState(false);
  const [siteCreateOpen, setSiteCreateOpen] = useState(false);
  const [sidebarFormSearch, setSidebarFormSearch] = useState('');
  const [rightFormSearch, setRightFormSearch] = useState('');
  const [user, setUser] = useState(initialUser || getStoredUser());
  const [certificateEmployee, setCertificateEmployee] = useState(null);

  useEffect(() => {
    const email = user?.email;
    if (!email) return undefined;
    let cancelled = false;
    (async () => {
      const refreshed = await refreshCurrentUser(email);
      if (!cancelled && refreshed) setUser(refreshed);
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.email]);

  useEffect(() => {
    setUserCreateOpen(false);
    setSiteCreateOpen(false);
  }, [activeView]);

  const companyName = user?.company_name || 'HINDBO';
  const companyLogoUrl = user?.company_logo_url ? apiUrl(user.company_logo_url) : null;
  const formCards = useMemo(() => getSafetyFormCards(companyName), [companyName]);
  const sidebarFormCards = useMemo(
    () => formCards.filter((card) => matchesFormSearch(card, sidebarFormSearch)),
    [formCards, sidebarFormSearch]
  );
  const homeFormCards = useMemo(
    () => formCards.filter((card) => matchesFormSearch(card, rightFormSearch)),
    [formCards, rightFormSearch]
  );
  const formSection = useMemo(
    () => ({ label: 'Safety Forms', cards: homeFormCards }),
    [homeFormCards]
  );
  const allSections = useMemo(
    () => [...ADMIN_SECTIONS, formSection],
    [formSection]
  );
  const allCards = useMemo(
    () => [...ADMIN_SECTIONS.flatMap((s) => s.cards), ...formCards],
    [formCards]
  );
  const activeCard = findActiveCard(activeView, ADMIN_SECTIONS, formCards);
  const isSubmittedFormsView = activeView === SUBMITTED_FORMS_VIEW;
  const isHazardsView = activeView === HAZARDS_CONTROL_VIEW;
  const isProfileView = activeView === CONFIGURE_PROFILE_VIEW;
  const isFormView = isSafetyFormView(activeView);
  const handleNavigate = (view) => {
    setActiveView(view);
    setMobileNavOpen(false);
  };

  const panelProps = {
    userCreateOpen,
    onUserCreateOpen: setUserCreateOpen,
    siteCreateOpen,
    onSiteCreateOpen: setSiteCreateOpen,
  };

  const headerProps = {
    activeView,
    userCreateOpen,
    siteCreateOpen,
    onUserCreateOpen: setUserCreateOpen,
    onSiteCreateOpen: setSiteCreateOpen,
    showNotificationsBell: isAboveSupervisorRole(user?.role),
    onOpenCertificateNotification: async (notification) => {
      const employeeId = notification?.employee_id;
      if (!employeeId) return;
      const fallback = {
        id: employeeId,
        name: notification.employee_name || 'Employee',
        email: notification.employee_email || '',
        certificates: notification.certificate_id
          ? [
              {
                id: notification.certificate_id,
                name: notification.certificate_name || 'Certificate',
                description: notification.description || '',
                issued_on: notification.issued_on || '',
                expires_on: notification.expires_on || '',
                notify: true,
              },
            ]
          : [],
      };
      try {
        const res = await fetch(apiUrl('/api/admin/clients'), {
          headers: { 'X-User-Email': user?.email || '' },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          const match = (data.clients || []).find(
            (row) => String(row.id) === String(employeeId)
          );
          if (match) {
            setCertificateEmployee(match);
            return;
          }
        }
      } catch {
        // Fall through to notification stub.
      }
      setCertificateEmployee(fallback);
    },
  };

  const showHeaderToolbar =
    activeView === 'home' ||
    isSubmittedFormsView ||
    isHazardsView ||
    isProfileView ||
    (activeView !== 'home' && activeCard && !isFormView);

  return (
    <UserProfileProvider email={user?.email}>
    <div className="relative h-screen bg-black text-white flex overflow-hidden">
      <MobileActionBar menuOpen={mobileNavOpen} onOpenMenu={() => setMobileNavOpen(true)}>
        {showHeaderToolbar ? <AdminHeaderActions {...headerProps} /> : null}
      </MobileActionBar>
      {mobileNavOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-black/70 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-label="Close navigation"
        />
      ) : null}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-gray-950 border-r border-gray-800 p-4 flex flex-col shrink-0 overflow-y-auto transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 lg:p-6 ${
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <button
          type="button"
          onClick={() => setMobileNavOpen(false)}
          className="absolute right-3 top-3 inline-flex h-10 w-10 items-center justify-center rounded-md border border-gray-700 text-xl text-gray-300 hover:text-white lg:hidden"
          aria-label="Close navigation"
        >
          ×
        </button>
        <div className="mb-6">
          {companyLogoUrl ? (
            <div className="mb-4 w-full h-16 rounded-md border border-gray-700 bg-gray-900 overflow-hidden flex items-center justify-center">
              <img
                src={companyLogoUrl}
                alt={`${companyName} logo`}
                className="max-h-full max-w-full object-contain"
              />
            </div>
          ) : null}
          <h1 className="text-xl font-bold truncate">{companyName}</h1>
          {user ? (
            <CollapsibleAccountInfo
              email={user.email}
              roleLabel="Admin"
              roleClassName="text-sky-200/90"
              profileActive={isProfileView}
              onConfigureProfile={() => handleNavigate(CONFIGURE_PROFILE_VIEW)}
            />
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => handleNavigate('home')}
          className={`w-full text-left px-4 py-2 rounded-md text-sm font-medium transition-colors mb-4 ${
            activeView === 'home'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:bg-gray-900 hover:text-white'
          }`}
        >
          Dashboard
        </button>
        {allSections.map((section) => {
          const isSafetyForms = section.label === 'Safety Forms';
          if (isSafetyForms) {
            return (
              <CollapsibleSidebarNav
                key={section.label}
                label={section.label}
                cards={sidebarFormCards}
                activeView={activeView}
                onSelect={handleNavigate}
                searchValue={sidebarFormSearch}
                onSearchChange={setSidebarFormSearch}
                searchPlaceholder="Search forms…"
                searchInputClass={sidebarSearchClass}
                emptyText={
                  sidebarFormSearch.trim() ? 'No forms match your search.' : 'No forms.'
                }
              />
            );
          }
          return (
            <CollapsibleSidebarNav
              key={section.label}
              label={section.label}
              cards={section.cards}
              activeView={activeView}
              onSelect={handleNavigate}
            />
          );
        })}
      </aside>

      {isFormView ? (
        <main className="relative flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden px-3 pb-3 pt-16 sm:px-4 lg:px-8 lg:pb-8 lg:pt-3">
          <div className="mx-auto w-full max-w-5xl flex flex-col flex-1 min-h-0">
            {showHeaderToolbar && <AdminHeaderToolbar {...headerProps} />}
            {activeView !== 'home' && !isProfileView && (activeCard || isSubmittedFormsView || isHazardsView)
              ? renderPanel(activeView, () => setActiveView('home'), companyName, user, panelProps)
              : null}
          </div>
        </main>
      ) : (
        <ScrollableRightPane
          className="flex-1 min-w-0"
          scrollClassName="px-2 sm:px-4 pt-16 pb-3 lg:py-3"
        >
          <div className="mx-auto w-full max-w-none">
            {showHeaderToolbar && <AdminHeaderToolbar {...headerProps} />}

            {activeView === 'home' && (
              <>
                <h2 className="text-2xl font-semibold mb-2">Admin Dashboard</h2>
                <p className="text-gray-400 text-xs mb-8">
                  {allCards.length} tasks — User/Workers, sites, forms, and safety.
                </p>
                {allSections.map((section) => (
                  <DashboardCardSection
                    key={section.label}
                    label={section.label}
                    cards={section.cards}
                    onOpenCard={setActiveView}
                    accent={section.label === 'User/Workers & Sites/Projects' ? 'sky' : 'amber'}
                    {...(section.label === 'Safety Forms'
                      ? {
                          searchValue: rightFormSearch,
                          onSearchChange: setRightFormSearch,
                        }
                      : {})}
                  />
                ))}
              </>
            )}

            {isProfileView && (
              <div className="border border-gray-800 rounded-lg bg-gray-950/70 p-4 md:p-5 min-h-full">
                <ConfigureProfilePanel onBack={() => setActiveView('home')} />
              </div>
            )}

            {activeView !== 'home' && !isProfileView && (activeCard || isSubmittedFormsView || isHazardsView) && (
              <div className="border border-gray-800 rounded-lg bg-gray-950/70 p-2.5 md:p-3 min-h-full">
                {renderPanel(activeView, () => setActiveView('home'), companyName, user, panelProps)}
              </div>
            )}
          </div>
        </ScrollableRightPane>
      )}
      {certificateEmployee ? (
        <CertificateUploadModal
          employee={certificateEmployee}
          onClose={() => setCertificateEmployee(null)}
          onSaved={() => setCertificateEmployee(null)}
        />
      ) : null}
    </div>
    </UserProfileProvider>
  );
}
